import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';

import { SalesService, WorkbookService } from '../../../../../../service/proxy';

interface SelectedWorksheetInput {
    worksheetName: string;
    headerIndex: number | null;
    startIndex: number | null;
    endIndex: number | null;
}

@Component({
    selector: 'sales-range-selection',
    templateUrl: './sales-range-selection.component.html',
    styleUrls: ['./sales-range-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        SalesService,
        WorkbookService
    ]
})
export class SalesRangeSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public filePath: string = '';
    public selectedWorksheets: SelectedWorksheetInput[] = [];
    public selectedPreview?: SelectedWorksheetInput;
    public firstTenRows: any[] = [];
    public lastTenRows: any[] = [];
    public loading: boolean = false;
    public isWideTable = false;

    constructor(
        private route: ActivatedRoute,
        private salesService: SalesService,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
    ) {}

    ngOnInit(): void {}

    onActivate(): void {
        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                // Always reload worksheet selections (may have changed if user went back to sheet-selection step)
                this.getWorksheetSelections(this.batchId);

                // Only fetch the batch (for filePath) on first activation.
                // On back-navigation filePath is already set — no need to hit the server again.
                if (!this.filePath) {
                    this.getBatch();
                }
            }
        });
    }

    getBatch(): void {
        this.salesService.getSalesBatch(this.batchId)
            .subscribe((result) => {
                this.filePath = `${result.filePath}//${result.storedName}`;
            }
        );
    }

    getWorksheetSelections(batchId: number): void {
        this.loading = true;

        this.salesService.getSalesWorksheetSelection(batchId)
            .subscribe({
                next: (result) => {
                    const latest = (result['results'] || [])
                        .filter((worksheet: any) => worksheet.isSelected)
                        .map((worksheet: any): SelectedWorksheetInput => {
                            return {
                                worksheetName: worksheet.worksheetName,
                                headerIndex: null,
                                startIndex: null,
                                endIndex: null
                            };
                        });

                    this.selectedWorksheets = latest;

                    // Load saved range mappings AFTER worksheet selections are populated
                    this.getImportRangeMapping();
                },
                error: (error) => {
                    this.errorService.handleError(error);
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    getImportRangeMapping(): void {
        this.salesService.getSalesImportRangeMapping(this.batchId)
            .subscribe((result) => {
                if (!result || !Array.isArray(result)) return;

                result.forEach((mapping: any) => {
                    const target = this.selectedWorksheets.find(w => w.worksheetName === mapping.worksheetName);
                    if (target) {
                        // Directly assign saved values (overwrite nulls)
                        target.headerIndex = mapping.headerIndex;
                        target.startIndex = mapping.startIndex;
                        target.endIndex = mapping.endIndex;
                    }
                });
            }
        );
    }

    getWorksheetSample(filePath: string, worksheetName: string): void {
        this.selectedPreview = this.selectedWorksheets.find(worksheet => worksheet.worksheetName === worksheetName);
        this.loading = true;
        this.isWideTable = false;

        this.workbookService.getSampleBySheetName(filePath, worksheetName)
            .subscribe({
                next: (result) => {
                    this.firstTenRows = result.firstTenRows || [];
                    this.lastTenRows = result.lastTenRows || [];

                    const headerRow = this.firstTenRows[0];
                    const colCount = headerRow?.rowValues?.length || 0;
                    this.isWideTable = colCount > 12;
                },
                error: (error) => {
                    if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        }
                        else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    isValid(): boolean {
        return true;
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const inputArray: any[] = this.selectedWorksheets.map(worksheet => ({
                worksheetName: worksheet.worksheetName,
                headerIndex: worksheet.headerIndex,
                startIndex: worksheet.startIndex,
                endIndex: worksheet.endIndex
            }));

            this.salesService.createSalesImportRangeMapping(this.batchId, inputArray).subscribe({
                next: () => {
                    this.toastService.show('Sales row mapping saved successfully', 'Success');

                    const mappingsChangedKey = `sales_mappings_changed_${this.batchId}`;
                    sessionStorage.setItem(mappingsChangedKey, 'true');

                    observer.next(true);
                    observer.complete();
                },
                error: (error: any) => {
                    this.errorService.handleError(error);

                    observer.next(false);
                    observer.complete();
                },
            });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}